import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { apiClient } from '@rti/shared';
import type { FeatureFlagPublic } from '@rti/shared';
import { useAuth } from '@/contexts/AuthContext';

interface FeatureFlagsContextValue {
  flags: FeatureFlagPublic[];
  getFlag: (key: string) => boolean;
  isLoading: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: [],
  getFlag: () => false,
  isLoading: true,
});

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isImpersonating, user } = useAuth();
  const [flags, setFlags] = useState<FeatureFlagPublic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setFlags([]);
      hasLoadedOnce.current = false;
      setIsLoading(false);
      return;
    }
    // Only report loading on the very first fetch. Consumers gate on this —
    // BillingPage returns null while it's true — so reporting it on a refetch
    // unmounts whatever the user was in the middle of, losing form state. A
    // refetch keeps serving the flags we already have until the new ones land.
    if (!hasLoadedOnce.current) {
      setIsLoading(true);
    }
    const impersonateId = isImpersonating && user?.user_id ? user.user_id : undefined;
    apiClient.getFeatureFlags(impersonateId)
      .then(data => setFlags(data))
      .catch(() => { if (!hasLoadedOnce.current) setFlags([]); })
      .finally(() => {
        hasLoadedOnce.current = true;
        setIsLoading(false);
      });
  }, [isAuthenticated, isImpersonating, user?.user_id]);

  const getFlag = (key: string): boolean => flags.some(f => f.feature_key === key);

  return (
    <FeatureFlagsContext.Provider value={{ flags, getFlag, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlagsContext = () => useContext(FeatureFlagsContext);
